import React from 'react'
import { t } from '@figma/i18n/translation'
import { authGenericGoBackAndTryAgain } from '@figma/i18n-dictionaries/en/web/auth'
import { isSamlOnlyLoginEnv } from '@web/environment'
import history from '@web/lib/history'
import { useEffectOnMount } from '@web/lib/hooks/use_effect_on_mount'
import { useSetAtom } from '@web/lib/jotai'
import platform from '@web/lib/platform'
import { TrackingContext } from '@web/o11y/analytics/tracking_context'
import { LoadTimer } from '@web/o11y/load_timer'
import { AppAuthAppType, AuthFormState } from '@web/redux/auth'

import { desktopApp } from 'lib/desktop_app'
import { passwordAtom } from 'shared/auth/auth_atoms'
import { useAuthService } from 'shared/auth/use_auth_service'
import { useAuthState } from 'shared/auth/use_auth_state'
import { useSessionRefreshCompletion } from 'shared/auth/use_session_refresh_completion'
import { VerifyHumanForm } from 'shared/auth/views/auth_view/auth_form/verify_human_form'
import type { AuthViewProps } from 'shared/auth/views/auth_view/auth_types'
import { TwoFactorForm } from 'shared/auth/views/auth_view/authentication/two_factor/two_factor'
import { JoinOrgForm } from 'shared/auth/views/auth_view/join_org/join_org'
import {
  ForgotPasswordForm,
  ResetPasswordForm,
  SentPasswordReset,
} from 'shared/auth/views/auth_view/password_reset/password_reset'

import { useMakePromptViaURL } from 'figma_app/lib/make/hooks/use_make_prompt_via_url'

import { performSsoRedirect } from './add_continuation_and_redirect'
import {
  AppAuthDesktopRequestForm,
  AppAuthGrantForm,
  AppAuthMobileRedeemForm,
  AppAuthRedeemForm,
} from './authentication/app_auth/app_auth'
import { AuthAccountPicker } from './authentication/auth_account_picker/auth_account_picker'
import { EmailOnlyForm } from './authentication/email_only/email_only'
import { EnableCookies } from './authentication/enable_cookies/enable_cookies_view'
import { GoogleSSOSignupActionRedirect } from './authentication/google_sso_redirect/google_sso_redirect'
import { SamlStartForm } from './authentication/sign_in_and_up/saml_start_form'
import { SignInForm } from './authentication/sign_in_and_up/sign_in_form'
import { SignUpForm } from './authentication/sign_in_and_up/sign_up_form'
import { SitesSSO } from './authentication/sites_sso/sites_sso'
import { SSOGateForm } from './authentication/sso_gate/sso_gate'
import { SsoSelectionForm } from './authentication/sso_selection/sso_selection'
import { ValidateCode } from './authentication/validate_code/validate_code'
import { MagicLinkCheckEmail } from './authentication/validate_email/magic_link_check_email'
import { ValidateEmail } from './authentication/validate_email/validate_email'
import { isTrustedTesterEmail } from './trusted_tester'

export function useHeaderAction(formState?: AuthFormState) {
  const authService = useAuthService()

  if (formState === AuthFormState.APP_AUTH_REDEEM) {
    // FIXME: This was disabled while enabling the @web/custom-hooks-must-memoize-return-values rule
    // eslint-disable-next-line @web/custom-hooks-must-memoize-return-values
    return () => {
      if (platform.isAndroidMirrorApp || platform.isInNativeApp) {
        history.redirect('/mobile-app')
      } else {
        authService.appAuth.redeemReset()
      }
    }
  }
}

/** Delay displaying header action text for a few seconds to prevent premature cancellations. */
export function useHeaderActionText(formState?: AuthFormState): string | undefined {
  const [headerActionText, setHeaderActionText] = React.useState<string | undefined>(undefined)
  const HEADER_ACTION_DELAY_MILLIS = 5000

  React.useEffect(() => {
    if (formState === AuthFormState.APP_AUTH_REDEEM) {
      const timer = setTimeout(() => {
        setHeaderActionText(t(authGenericGoBackAndTryAgain))
      }, HEADER_ACTION_DELAY_MILLIS)

      return () => clearTimeout(timer)
    } else {
      setHeaderActionText(undefined)
    }
  }, [formState])

  return headerActionText
}

/**
 * The root component of the Auth app. Renders all the different forms based on the current formState.
 */
export function AuthView(props: Parameters<typeof _AuthView>[0]) {
  return (
    <LoadTimer label="AuthView">
      <_AuthView {...props} />
    </LoadTimer>
  )
}

function _AuthView({ signupHeaderOverride, ...props }: AuthViewProps) {
  const authService = useAuthService()
  const [redirectUrl, setRedirectUrl] = useAuthState('redirectUrl')
  const [formState] = useAuthState('formState')

  useSessionRefreshCompletion()

  // This hook call enables the Make Prompt via URL feature for logged-out users
  useMakePromptViaURL(redirectUrl, setRedirectUrl)

  React.useEffect(() => {
    return () => {
      authService.authLifecycle.clearError()
      authService.authLifecycle.showForm(AuthFormState.SIGN_UP)
    }
  }, [authService])

  const renderAuthView = () => {
    if (!navigator.cookieEnabled) {
      return <EnableCookies />
    }
    switch (formState) {
      case AuthFormState.SIGN_IN:
      case AuthFormState.SIGN_UP:
      case AuthFormState.RESET_PASSWORD:
      case AuthFormState.VALIDATE_CODE:
      case AuthFormState.SAML_START:
      case AuthFormState.EMAIL_ONLY:
      case AuthFormState.JOIN_ORG:
      case AuthFormState.ACCOUNT_PICKER:
      case AuthFormState.VERIFY_HUMAN:
      case AuthFormState.GOOGLE_SSO_SIGNUP_ACTION_REDIRECT:
      case AuthFormState.TWO_FACTOR:
      case AuthFormState.SITES_SSO:
        return <AuthForms {...props} signupHeaderOverride={signupHeaderOverride} />
      case AuthFormState.FORGOT_PASSWORD:
        return <ForgotPasswordForm {...props} />
      case AuthFormState.SENT_PASSWORD_RESET:
        return <SentPasswordReset {...props} />
      case AuthFormState.VALIDATE_EMAIL:
        return <ValidateEmail />
      case AuthFormState.APP_AUTH_GRANT:
        return <AppAuthGrantForm {...props} />
      case AuthFormState.APP_AUTH_REDEEM:
        return <AppAuthRedeemForm {...props} />
      case AuthFormState.APP_AUTH_MOBILE_REDEEM:
        return <AppAuthMobileRedeemForm {...props} />
      case AuthFormState.SSO_GATE:
        return <SSOGateForm {...props} />
      case AuthFormState.SSO_SELECTION:
        return <SsoSelectionForm {...props} />
      case AuthFormState.CHECK_EMAIL_MAGIC_LINK_SIGN_IN_AFTER_PASSWORD:
        return <MagicLinkCheckEmail />
    }

    return null
  }

  return (
    <div data-component-name="auth-view" style={{ display: 'contents' }} translate="no">
      <TrackingContext name="AuthView" properties={{ formState }}>
        {renderAuthView()}
      </TrackingContext>
    </div>
  )
}

export interface AuthFormsControlsCollection extends HTMLFormControlsCollection {
  password: HTMLInputElement
  totp_key: HTMLInputElement
  email: HTMLInputElement
}

/**
 * AuthForms renders the sign in, sign up, extra information sign up, reset password, and two factor
 * authentication forms depending on auth.formState
 */
export function AuthForms({ signupHeaderOverride, ...props }: AuthViewProps) {
  const authService = useAuthService()
  const [, setGoogleIdToken] = useAuthState('googleIdToken')
  const [email] = useAuthState('email')
  const [formState] = useAuthState('formState')
  const [authOrigin] = useAuthState('origin')
  const [accountPicker] = useAuthState('accountPicker')

  const setPassword = useSetAtom(passwordAtom)

  const { id, header } = props

  // App-store reviewers are exempt from the SAML-only policy server-side, so the
  // UI has to let them reach the password form too.
  const isTrustedTester = isTrustedTesterEmail(email)

  useEffectOnMount(() => {
    // NOTE: We only want to clear the password and the Google ID token
    // when the user is not in an intermediate step. The following cases in the condition
    // are intermediate steps where we don't want to clear the password and the Google ID token.
    if (
      formState !== AuthFormState.GOOGLE_SSO_SIGNUP_ACTION_REDIRECT &&
      formState !== AuthFormState.VERIFY_HUMAN
    ) {
      setGoogleIdToken(null)
      setPassword(null)
    }
  })

  const onFormSubmit = () => {
    const isGoogleOneTap = authOrigin?.startsWith('google_one_tap')

    const formId: string = id
    const elements: AuthFormsControlsCollection = (
      document.getElementById(formId) as HTMLFormElement
    ).elements as AuthFormsControlsCollection

    if (elements.password && !isGoogleOneTap) {
      // set password & clear google ID token if this isn't part of the Google One Tap flow
      setPassword(elements.password.value)
      setGoogleIdToken(null)
    }

    // In the gov environment, we only want to use the SAML_START
    // authentication type, so we override the action (though we still allow
    // the VALIDATE_CODE action and allowlisted emails)
    let submitFormState = formState
    if (
      window.INITIAL_OPTIONS.cluster_name === 'gov' &&
      submitFormState !== AuthFormState.VALIDATE_CODE &&
      !window.origin.startsWith('https://admin.') &&
      !isTrustedTester
    ) {
      submitFormState = AuthFormState.SAML_START
    }

    if (
      isSamlOnlyLoginEnv() &&
      submitFormState !== AuthFormState.VALIDATE_CODE &&
      !isTrustedTester
    ) {
      submitFormState = AuthFormState.SAML_START
    }

    switch (submitFormState) {
      case AuthFormState.SIGN_IN:
        authService.signIn.passwordAfterHumanVerification({ formId })
        break
      case AuthFormState.RESET_PASSWORD:
        authService.password.confirmReset({ formId })
        break
      case AuthFormState.SAML_START:
        authService.signIn.viaSaml({ formId }).then(performSsoRedirect)
        break
      case AuthFormState.SIGN_UP:
        authService.signUp.trackReferralSignupSubmitted({ formId })
        authService.signUp.submitAfterHumanVerification({ formId })
        break
      case AuthFormState.EMAIL_ONLY:
        authService.signIn.viaEmailOnly({ formId }).then(performSsoRedirect)
        break
    }
  }

  if (formState === AuthFormState.ACCOUNT_PICKER && accountPicker) {
    return <AuthAccountPicker authOrigin={authOrigin} />
  } else if (formState === AuthFormState.SITES_SSO) {
    return <SitesSSO />
  } else if (desktopApp) {
    // We ignore the state machine (i.e. the current formState value) in this case.
    //
    // It may be worthwhile to separate out the "Request" / "Redeem"
    // components and routes from the main AppAuth, as these aren't
    // reachable via transitions from other formState values
    //
    // Request is the landing page while logged out in Desktop
    // Redeem can only be opened by a specific figma:// link
    return <AppAuthDesktopRequestForm appType={AppAuthAppType.DESKTOP} {...props} />
  } else if (
    window.INITIAL_OPTIONS.cluster_name === 'gov' &&
    formState !== AuthFormState.VALIDATE_CODE &&
    !window.origin.startsWith('https://admin.') &&
    !isTrustedTester
  ) {
    // Only show a SAML prompt in the gov environment, but allow Google sign-in on the admin page or for allowlisted emails, where SAML isn't supported

    return <SamlStartForm onFormSubmit={onFormSubmit} {...props} />
  } else if (
    isSamlOnlyLoginEnv() &&
    formState !== AuthFormState.VALIDATE_CODE &&
    !isTrustedTester
  ) {
    return <SamlStartForm onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.SIGN_IN) {
    return (
      <TrackingContext name="Sign In">
        <SignInForm onFormSubmit={onFormSubmit} {...props} />
      </TrackingContext>
    )
  } else if (formState === AuthFormState.SIGN_UP) {
    return (
      <TrackingContext name="Sign Up">
        <SignUpForm
          {...props}
          onFormSubmit={onFormSubmit}
          header={signupHeaderOverride ?? header}
        />
      </TrackingContext>
    )
  } else if (formState === AuthFormState.GOOGLE_SSO_SIGNUP_ACTION_REDIRECT) {
    return (
      <TrackingContext name="Google SSO Redirect">
        <GoogleSSOSignupActionRedirect formId={id} />
      </TrackingContext>
    )
  } else if (formState === AuthFormState.RESET_PASSWORD) {
    return <ResetPasswordForm onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.VALIDATE_CODE) {
    return <ValidateCode onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.SAML_START) {
    return <SamlStartForm onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.EMAIL_ONLY) {
    return <EmailOnlyForm onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.JOIN_ORG) {
    return <JoinOrgForm onFormSubmit={onFormSubmit} {...props} />
  } else if (formState === AuthFormState.VERIFY_HUMAN) {
    return <VerifyHumanForm {...props} />
  } else if (formState === AuthFormState.TWO_FACTOR) {
    return <TwoFactorForm onFormSubmit={onFormSubmit} {...props} />
  }
  return null
}
